import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReturnButton from './ReturnButton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { codeToEmoji, inversedCData } from '../flags';
import { getSectorEmoji } from '../sectorData';
import { majorMap, minorMap, certMap } from '../mapping';
import { LinkedinIcon, MapPinIcon, Factory, School, PhoneIcon, Trophy, MailIcon, Briefcase, GraduationCap, BriefcaseBusiness, MapPinHouse, BriefcaseIcon, GraduationCapIcon, HomeIcon, Phone, Mail, Linkedin, MapPin, X, ExternalLink} from 'lucide-react';

export const getInitials = (name) => {
  if (typeof name === 'undefined') return '?';
  const names = name.split(' ');
  const initials = names.map((n) => n[0]);
  return initials.join('');
};

const ProfilePage = ({ users, isModal = false, userID=null, setIsOpen=null, setPath=null}) => {
  const { id } = useParams();
  let user = undefined;
  let id_ = undefined;
  const navigate = useNavigate();
  let flag = false;
  if (id) {
    id_ = id;
    user = users.find((user) => user.id === id);
    flag = true;
  } else if (userID) {
    id_ = userID;
    user = users.find((user) => user.id === userID);
    flag = true;
  }
  

  if (!flag) {
    return  (<div className="flex justify-center">
    <Card className="w-[350px]">
      <CardHeader className="text-center">
        <CardTitle>Loading...</CardTitle>
      </CardHeader>
    </Card>
  </div>)
  }



  const handleOpenInNewTab = () => {
    window.open(`/profile/${id_}`, '_blank');
  };

  const handleClose = () => {
    navigate('/');
  };


  let parsedGrad = "";
  if (user && user.gradYear) {
    parsedGrad = "'" + user.gradYear.toString().slice(-2);
  }

  if (!user) {
    return (
      <div className="flex justify-center">
        <Card className="w-[350px]">
          <CardHeader className="text-center">
            <CardTitle>Getting user info now...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <ReturnButton />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user.name) {
    return (
      <div className="flex justify-center">
        <Card className="w-[350px]">
          <CardHeader className="text-center">
            <CardTitle>No User Exists With Id: {id}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <ReturnButton />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const InfoItem = ({ icon: Icon, label, value, href }) => {
    if (!value) return null;
    
    const content = (
      <div className="flex items-center gap-2 text-gray-600">
        <Icon className="w-4 h-4" />
        <span className="font-medium">{label}:</span>
        <span>{value}</span>
      </div>
    );

    return href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
        {content}
      </a>
    ) : content;
  };

  let academicsSet = new Set();
  if (user.majors) {
    user.majors.forEach((major) => academicsSet.add(majorMap.get(major)));
  }
  if (user.minors) {
    user.minors.forEach((minor) => academicsSet.add(minorMap.get(minor)));
  }
  if (user.certificates) {
    user.certificates.forEach((cert) => academicsSet.add(certMap.get(cert)));
  }
  let academics = null;
  if (academicsSet.size > 0) {
    academics = Array.from(academicsSet).join(", ");
  }

  let lst = [];
  if (user.location) {
    lst.push(user.location);
  }
  if (user.sector) {
    lst.push(user.sector);
  }
  let locationSector = null;
  if (lst.length > 0) {
    locationSector = lst.join(" - ");
  }

  let calcMajor = null;
  if (user.majors && user.majors.length > 0) {
    if (user.majors.length > 1) {
      calcMajor = "Majors: ";
    } else {
      calcMajor = "Major: ";
    }
    let lst = []
    user.majors.forEach(major => {
      lst.push(major);
    })
    calcMajor = calcMajor + lst.join(", ")
  }

  let majors = null;
  if (user.majors) {
    majors = user.majors.join(", ")
  }

  let minors = null;
  if (user.minors) {
    minors = user.minors.join(", ")
  }

  let certificates = null;
  if (user.certificates) {
    certificates = user.certificates.join(", ")
  }

  const handleLinkedInClick = (e, url) => {
    e.preventDefault();
    window.open(url, '_blank');
  };

  const profileContent = (
    <Card className="max-w-6xl min-w-[40vw] mx-auto p-8">
      {/* Action buttons */}
      {isModal && (
        <div className="flex w-full flex-row justify-end">
        <button
          onClick={handleOpenInNewTab}
          className="p-2 rounded-md hover:bg-gray-100 transition-colors"
          title="Open in new tab"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
      </div>
      )}
      {!isModal && <ReturnButton />
      }

      
      
      {/* Profile Header */}
      <div className="text-center mb-8">
        <Avatar className="w-36 h-36 mx-auto mb-4">
          <AvatarImage src={user.image} />
          <AvatarFallback style={{ fontSize: "24px" }}>{getInitials(user.name)}</AvatarFallback>
        </Avatar>
        <h1 className="text-3xl font-bold mb-2">{user.name}</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-8">
        <div className={`${!isModal || (user.comments || user.bio) ? 'md:max-w-1/2 tag!' : "w-full"}`}>
        <div className="flex-shrink-0 w-full w-fit text-slate-800">
          {/* CONTACT INFO */}
          { (user.email || user.phone || user.linkedIn) && (
          <Card className="flex flex-col border-b-2 mb-4 p-4 text-inherit bg-slate-100 space-y-1">

            {user.email && <div className="flex shrink-1 flex-row items-center">
              <Mail className="w-4 h-4 mr-1"/>&nbsp;
              <span>{user.displayEmail || user.email}</span>
            </div>}

            {user.phone && <div className="flex shrink-1 flex-row items-center">
              <Phone className="w-4 h-4 mr-1 "/>&nbsp;
              <span>{user.phone}</span>
            </div>}

            {user.linkedIn && <div className="flex shrink-1 flex-row items-center">
              <Linkedin className="w-4 h-4 mr-1 "/>&nbsp;
              <a href={user.linkedIn}>{user.linkedIn.split("com")[1]}</a>
            </div>}

          </Card>)}
          {/* JOB INFO */}
          {(user.currentRole || user.currentCompany || user.location || (user.sectors && user.sectors.length > 0)) && (<Card className="flex flex-col border-b-2 mb-4 p-4 text-inherit bg-slate-100 space-y-1">

          {user.currentCompany && user.currentRole && (
            <div className="flex shrink-1 flex-row items-start">
              <Briefcase className="w-4 h-4 mr-1 mt-1"/>&nbsp;
              <div >{user.currentRole} at {user.currentCompany}</div>
            </div>
                
              )}
              {!user.currentCompany && user.currentRole && (
                <div className="flex shrink-1 flex-row items-start">
                <Briefcase className="w-4 h-4 mr-1 mt-1"/>&nbsp;
                <div >{user.currentRole}</div>
              </div>
              )}
              {user.currentCompany && !user.currentRole && (
                <div className="flex shrink-1 flex-row items-start">&nbsp;
                <Briefcase className="w-4 h-4 mr-1 mt-1"/>
                <div >{user.currentCompany}</div>
              </div>
              )}
              {user.location && user.nearestHubCity && (
                <div className="flex shrink-1 flex-row items-start">
                  <MapPinIcon className="w-4 h-4 mt-1"/>&nbsp;
                  <span>{user.location} (near {user.nearestHubCity})</span>
                </div>
                )}
              {user.location && !user.nearestHubCity && (
                <div className="flex shrink-1 flex-row items-center">
                  <MapPinIcon className="w-4 h-4 mr-1"/>&nbsp;
                  <span>{user.location}</span>
                </div>
              )}
              {user.sectors && user.sectors.length > 0 && (
                <div className="flex flex-row w-full items-start">
                <Factory className="h-4 w-4 mt-1"/> &nbsp;
                <div className="flex flex-col items-start">
                  {user.sectors.map((code, index) => (
                    <span key={index} className="flex items-center ml-1 justify-center item-start">
                      {getSectorEmoji(code)}&nbsp;{code}
                    </span>
                  ))}
                </div>
                </div>
              )}
            </Card>)}

              {/* ACADEMIC INFO */}

            { (user.gradYear || (user.majors && user.majors.length > 0) || (user.minors && user.minors.length > 0) || (user.certificates && user.certificates.length > 0) || user.extracurriculars) && ( 
            <Card className="flex flex-col border-b-2 p-4 mb-4 text-inherit bg-slate-100 space-y-1">
              <div className="flex shrink-1 flex-row items-center">
                <GraduationCap className="w-4 h-4 mr-1"/>&nbsp;
                <span>Class of {user.gradYear}</span>
              </div>
              {user.majors && user.majors.length > 0 && (
              <div className="flex shrink-1 flex-row items-start">
                <School className="w-4 h-4 mt-1 mr-1"/>
                &nbsp;
                {user.majors && user.majors.length == 1 && (
                  <span>Major: {user.majors[0]}</span>
                )}
                {user.majors && user.majors.length > 1 && (
                  <span>Majors: {majors}</span>
                )}
              </div>
              )}
              {user.minors && user.minors.length > 0 && (
              <div className="flex shrink-1 flex-row items-start">
                <School className="w-4 h-4 mt-1 mr-1"/>
                &nbsp;
                {user.minors && user.minors.length == 1 && (
                  <span>Minor: {user.minors[0]}</span>
                )}
                {user.minors && user.minors.length > 1 && (
                  <span>Minors: {minors}</span>
                )}
                  
              </div>
              )}
              {user.certificates && user.certificates.length > 0 && (
              <div className="flex shrink-1 flex-row items-start">
                <School className="w-4 h-4 mt-1 mr-1"/>
                &nbsp;
                {user.certificates && user.certificates.length == 1 && (
                  <span>Certificate: {user.certificates[0]}</span>
                )}
                {user.certificates && user.certificates.length > 1 && (
                  <span>Certificates: {certificates}</span>
                )}
              </div>
              )}
              {user.extracurriculars && (
                <div className="flex shrink-1 flex-row items-start">
                <Trophy className="w-4 h-4 mt-1"/>&nbsp;
                Other Clubs:&nbsp;{user.extracurriculars}
              </div>
              )}

            </Card>)}
          {/* BACKGROUND INFO */}

          { (user.hometown || (user.nationalities && user.nationalities.length > 0)) && (
          <Card className="flex flex-col justify-start border-b-2 p-4 mb-4 text-inherit bg-slate-100 space-y-1">

          {user.hometown && (
              <div className="flex shrink-1 flex-row items-center">
                <HomeIcon className="w-4 h-4 mr-1"/>&nbsp;
                <span>{user.hometown}</span>
              </div>
            )}
            {user.nationalities && user.nationalities.length > 0 && (
              <div className="flex flex-row items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-house"><path d="M15 22a1 1 0 0 1-1-1v-4a1 1 0 0 1 .445-.832l3-2a1 1 0 0 1 1.11 0l3 2A1 1 0 0 1 22 17v4a1 1 0 0 1-1 1z"/><path d="M18 10a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 .601.2"/><path d="M18 22v-3"/><circle cx="10" cy="10" r="3"/></svg>
                &nbsp;
                {user.nationalities.map((code, index) => (
                  <span key={index} className="flex items-center ml-1 justify-center text-base">
                    {inversedCData[code]}&nbsp;{codeToEmoji(code)}
                    {index < user.nationalities.length - 1 && ', '}
                  </span>
                ))}
              </div>
            )}

          </Card>)}
          </div>
        </div>
        {(user.bio || user.comments) && (
        <div className="flex-grow min-w-[100px]">
          {user.bio && (
          <div className="mb-4">
            <div className="font-semibold text-lg">Hobbies and Notes</div>
            <p>{user.bio}</p>
          </div>
          )}
          {user.comments && (
          <div>
            <div className="font-semibold text-lg">Favorite Wayne Memory(ies)</div>
            <p>{user.comments}</p>
          </div>
          )}
        </div>)}
      </div>
      {user.lastUpdated && 
            <div className="float-right">Last Updated: {user.lastUpdated.slice(5,7).concat("/", user.lastUpdated.slice(8,10),"/",user.lastUpdated.slice(0,4))}</div>
            }
      </Card>
    );

    const handleBackdropClick = (e) => {
      // Only close if clicking the backdrop (not the modal content)
      if (e.target === e.currentTarget) {
        setIsOpen(false);
        window.history.pushState({}, '', '/');
        setPath("/");
      }
    };

    if (isModal) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[20000] overflow-y-auto" onClick={handleBackdropClick}>
          <div className="min-h-screen flex items-center justify-center p-4 w-full my-5" onClick={handleBackdropClick}>
            {profileContent}
          </div>
        </div>
      );
    }

    return profileContent
  };


export default ProfilePage;